import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-generic-autocomplete',
  templateUrl: './generic-autocomplete.component.html',
  styles: [`    
    :host ::ng-deep .mat-form-field {
      width: 100%;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: GenericAutocompleteComponent,
      multi: true
    }
  ]
})
export class GenericAutocompleteComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() dataSource: any[] = [];
  @Input() valueField: string = 'id';
  @Input() displayField: string = 'nombre';
  @Input() placeholder: string = 'Seleccione una opción';
  @Input() disabled: boolean = false;
  @Input() initialText: string = '';

  @Input() selectedValue: any;
  @Output() selectedValueChange = new EventEmitter<any>();

  control = new FormControl();
  filteredOptions!: Observable<any[]>;
  
  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : value?.[this.displayField])),
      map(name => {
        if (name) {
          return this._filter(name);
         }
       return this.dataSource ? this.dataSource.slice() : [];
      })
    );

    this.control.valueChanges.subscribe(value => {
      this.handleInputChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && this.dataSource) {
      this.setSelectedValue();
    }
    if (changes['disabled']) {
      this.setDisabledState(this.disabled);
    }
  }

  private handleInputChange(value: any): void {
    if (value === null || value === '' || typeof value === 'string') {
      this.onChange(null);
    }
  }

  private setSelectedValue(): void {
    if (this.selectedValue !== undefined && this.selectedValue !== null) {
        const selectedItem = this.dataSource.find(item => item[this.valueField] === this.selectedValue);
        if (selectedItem) {
            this.control.setValue(selectedItem);
        } else if (this.initialText) {
            this.control.setValue({ [this.displayField]: this.initialText });
        } else {
            this.control.setValue(null);
        }
    } else {
        this.control.setValue(this.initialText ? { [this.displayField]: this.initialText } : null);
    }
  }

  private _filter(name: string): any[] {
    const filterValue = name.toLowerCase();
    return this.dataSource.filter(option =>
      option[this.displayField].toLowerCase().includes(filterValue)
    );
  }

  displayFn(option: any): string {
    return option && option[this.displayField] ? option[this.displayField] : '';
  }

  onOptionSelected(event: any): void {
    const selected = event.option.value;
    if (selected) {
      this.selectedValue = selected[this.valueField];
      this.selectedValueChange.emit(this.selectedValue);
      this.onChange(this.selectedValue);
    } else {
      this.selectedValue = null;
    }
    this.onTouched();
  }

  writeValue(value: any): void {
    this.selectedValue = value;
    this.setSelectedValue();
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  limpiar(): void {
    this.control.setValue(null);
    this.selectedValueChange.emit(null);
    this.onTouched();
  }

  onBlur(): void {
    const currentValue = this.control.value;

    if (typeof currentValue === 'string' && currentValue.trim() !== '') {
      const exactMatch = this.dataSource.find(
        option => option[this.displayField].toLowerCase() === currentValue.trim().toLowerCase()
      );

      if (exactMatch) {
        this.control.setValue(exactMatch, { emitEvent: false });
        this.onOptionSelected({ option: { value: exactMatch } });
      } else {
        this.control.setValue(null);
        this.selectedValueChange.emit(null);
      }
    }
  }
}